import { useState, type ReactNode } from "react";
import { ChevronDown, X } from "lucide-react";

import { cx } from "../../lib/cx";
import { useReducedMotion } from "../../hooks/useReducedMotion";
import { useIsMobile } from "../../hooks/useIsMobile";

interface FloatingPanelProps {
  /** Icon shown on the collapsed chip. */
  icon: ReactNode;
  title: string;
  /** aria-label for the collapsed trigger. */
  openLabel: string;
  collapseLabel: string;
  defaultOpen?: boolean;
  /** Extra header content (e.g. a "show all" reset), right-aligned. */
  headerAccessory?: ReactNode;
  /** Tailwind width class for the open card (desktop only). */
  widthClass?: string;
  children: ReactNode;
  className?: string;
}

/**
 * A panel that floats over the map. Collapsed, it's a compact icon chip so the
 * map stays uncovered (brief §4, §6). Expanded, it's a translucent card on the
 * desktop and a bottom sheet on mobile (brief §13). Open/close respects
 * `prefers-reduced-motion`.
 */
export function FloatingPanel({
  icon,
  title,
  openLabel,
  collapseLabel,
  defaultOpen = false,
  headerAccessory,
  widthClass = "w-64",
  children,
  className,
}: FloatingPanelProps) {
  const isMobile = useIsMobile();
  // Never auto-open on mobile — a sheet covering the map on load is disorienting.
  const [open, setOpen] = useState(
    () => defaultOpen && !window.matchMedia("(max-width: 767px)").matches,
  );
  const reduced = useReducedMotion();

  const PanelHeader = (
    <header className="flex items-center justify-between gap-2 border-b border-border-subtle px-3 py-2">
      <div className="flex items-center gap-2 text-sm font-semibold text-text-primary">
        {icon}
        <span>{title}</span>
      </div>
      <div className="flex items-center gap-1">
        {headerAccessory}
        <button
          type="button"
          aria-label={collapseLabel}
          title={collapseLabel}
          onClick={() => setOpen(false)}
          className="inline-flex h-6 w-6 items-center justify-center rounded text-text-tertiary transition-colors hover:bg-surface-sunken hover:text-text-primary"
        >
          {isMobile ? <X size={16} /> : <ChevronDown size={16} />}
        </button>
      </div>
    </header>
  );

  if (!open) {
    return (
      <button
        type="button"
        data-map-overlay="true"
        aria-label={openLabel}
        title={openLabel}
        onClick={() => setOpen(true)}
        className={cx(
          "inline-flex items-center gap-2 rounded-panel border border-border-subtle bg-surface-raised/80 px-3 py-2 shadow-md backdrop-blur",
          "text-sm text-text-secondary transition-colors hover:text-accent",
          className,
        )}
      >
        {icon}
        <span className="font-medium">{title}</span>
      </button>
    );
  }

  // Mobile: bottom sheet with a dismiss backdrop.
  if (isMobile) {
    return (
      <>
        <div
          className="fixed inset-0 z-40 bg-black/40"
          onMouseDown={() => setOpen(false)}
          aria-hidden
        />
        <section
          aria-label={title}
          data-map-overlay="true"
          className={cx(
            "fixed inset-x-0 bottom-0 z-40 flex max-h-[80vh] flex-col rounded-t-panel border-t border-border-subtle bg-surface-raised shadow-lg",
            reduced ? "" : "transition-transform duration-200",
          )}
        >
          {PanelHeader}
          <div className="overflow-y-auto px-3 py-2">{children}</div>
        </section>
      </>
    );
  }

  // Desktop: inline translucent card.
  return (
    <section
      data-map-overlay="true"
      className={cx(
        widthClass,
        "overflow-hidden rounded-panel border border-border-subtle bg-surface-raised/85 shadow-md backdrop-blur",
        reduced ? "" : "transition-all duration-200",
        className,
      )}
      aria-label={title}
    >
      {PanelHeader}
      <div className="max-h-[70vh] overflow-y-auto px-3 py-2">{children}</div>
    </section>
  );
}
